import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Users, Music, LogOut, Disc3, Menu, X, Settings, BarChart3, Stethoscope } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ErrorBoundary } from './ErrorBoundary';
import { ConnectivityPill } from './ConnectivityPill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEARTBEAT_INTERVAL_MS = 30_000;

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Presence heartbeat — pings /api/analytics/heartbeat every 30s while any
  // AdminLayout page is open. Drives the "Clients playing right now" gauge
  // on the superadmin dashboard. Silent-fails on network errors — presence
  // is best-effort and must never disrupt the console.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const ping = () => {
      // MusicContext writes `window.__spinItUpIsLive` whenever audio is
      // playing OR a crossfade is mid-transition — that's our "Live" gate.
      // Send it with every heartbeat so the server can log real playback
      // time (powers "Hours online" analytics).
      const isPlaying = typeof window !== 'undefined' && window.__spinItUpIsLive === true;
      axios.post(`${API}/analytics/heartbeat`, { is_playing: isPlaying }, { withCredentials: true }).catch(() => {});
    };
    ping();
    const timer = setInterval(() => {
      if (!cancelled && document.visibilityState !== 'hidden') ping();
    }, HEARTBEAT_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { to: '/admin/clients', icon: Users, label: 'Venues' },
    { to: '/admin/playlists', icon: Music, label: 'Playlists' },
    { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    ...(user?.role === 'superadmin' ? [{ to: '/admin/forensics', icon: Stethoscope, label: 'Forensics' }] : []),
    { to: '/admin/requests', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="h-screen flex bg-[#000000] overflow-hidden">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between p-4 glass-surface">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FF007F] rounded-full flex items-center justify-center animate-logo-pulse">
            <Disc3 className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="font-bold text-white tracking-wider text-sm" style={{ fontFamily: 'Outfit' }}>MusicBox</span>
            <span className="text-[10px] text-[#39FF14] ml-2">Host</span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="hover:bg-white/10"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
        </Button>
      </header>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/70" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Mobile Slide-out Menu */}
      <aside className={`
        lg:hidden fixed top-14 left-0 bottom-0 z-40 w-64 bg-[#000000] border-r border-white/5 flex flex-col
        transform transition-transform duration-200 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <nav className="flex-1 px-4 py-4">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.exact}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                  data-testid={`nav-mobile-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-white/5">
          <Button variant="ghost" onClick={handleLogout} className="w-full justify-start gap-3 px-4 py-3 text-[#FF3B3B] hover:bg-[#FF3B3B]/10 rounded-xl" data-testid="mobile-logout-button">
            <LogOut className="w-5 h-5" /> Log out
          </Button>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 bg-[#000000] border-r border-white/5 flex-col">
        <div className="p-5 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF007F] rounded-full flex items-center justify-center animate-logo-pulse">
            <Disc3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="text-base font-extrabold text-white tracking-wider" style={{ fontFamily: 'Outfit' }}>MusicBox</span>
            <p className="text-[10px] text-[#39FF14] tracking-widest">HOST PANEL</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-2">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-3 border-t border-white/5 space-y-2">
          <div className="flex items-center justify-between px-1">
            <ConnectivityPill variant="sidebar" />
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03]">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-[#FF007F]/20 text-[#FF007F] font-bold text-sm">
                {user?.name?.charAt(0) || 'A'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
              <p className="text-[10px] text-[#606060] truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} className="w-full justify-start gap-3 px-4 py-2.5 text-[#FF3B3B] hover:bg-[#FF3B3B]/10 rounded-xl text-sm" data-testid="logout-button">
            <LogOut className="w-4 h-4" /> Log out
          </Button>
        </div>
      </aside>

      {/* Main Content
          NB: `overflow-y-auto` is critical — the parent wrapper above is
          `h-screen overflow-hidden` for layout sanity, so this is the ONE
          element that owns vertical scroll for the entire admin shell.
          Removing it breaks scroll on every admin page (clients, menu,
          schedule, etc.). */}
      <main className="flex-1 overflow-y-auto pt-14 lg:pt-0">
        <ErrorBoundary scope="the admin console">
          {children}
        </ErrorBoundary>
      </main>
    </div>
  );
};

export default AdminLayout;
